import { useRef, useEffect, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import AiGenerativeTextSummaryIcon from "@atlaskit/icon/core/ai-generative-text-summary";
import LinkIcon from "@atlaskit/icon/core/link";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { Source } from "./ai-summary";
import { useAISummaryContext } from "./ai-summary-context";
import { AISummaryFooter } from "./ai-summary-footer";
import { MarkdownRenderer } from "./renderer/markdown-renderer";

const citationIconButtonStyle = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "flexShrink": 0,
    "lineHeight": 1,
    "height": token("space.250"),
    "maxWidth": "44px",
    "borderRadius": token("space.050"),
    "backgroundColor": wacToken("color.extended.blue100"),
    "color": wacToken("color.extended.blue500"),
    "border": "none",
    "padding": token("space.050"),
    "marginInlineStart": token("space.050"),
    "cursor": "pointer",
    "position": "relative",
    "top": "-1px",
    "transition": "background-color 0.2s ease",

    "&:hover": {
        backgroundColor: wacToken("color.extended.blue200")
    }
});

const linkIconStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    pointerEvents: "none",
    border: "none !important"
});

const sourceTextStyle = css({
    display: "inline-flex",
    alignItems: "center",
    font: token("font.body"),
    lineHeight: 1
});

const CitationIcon = ({
    citations,
    sources,
    onFilterSources,
    relatedSourceIndexes
}: {
    citations: string;
    sources?: Source[];
    onFilterSources: (sourceIndexes: number[]) => void;
    relatedSourceIndexes: number[];
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const identifier = citations.split(",").length;
    const sourceTitle = sources?.[identifier - 1]?.title;
    const sourceAriaLabel = sourceTitle || `Source ${identifier}`;

    const handleClick = () => {
        const sourceIndex = identifier - 1; // Convert 1-based citation to 0-based array index
        const sourceIndexesToFilter =
            relatedSourceIndexes.length > 0
                ? relatedSourceIndexes
                : [sourceIndex];

        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "searchAiSummaryInlineSourceFilter",
            source: "searchAiSummary",
            attributes: {
                sourceIndex: sourceIndex,
                sourceIndexes: sourceIndexesToFilter,
                sourceTitle: sources?.[sourceIndex]?.title,
                sourceUrl: sources?.[sourceIndex]?.url
            }
        }).fire();

        onFilterSources(sourceIndexesToFilter);
    };

    return (
        <button
            type="button"
            aria-label={sourceAriaLabel}
            css={citationIconButtonStyle}
            onClick={handleClick}
        >
            <LinkIcon
                size="medium"
                color={token("color.icon.brand")}
                css={linkIconStyle}
                label=""
            />
            {identifier > 1 && (
                <span css={sourceTextStyle}>· {identifier}</span>
            )}
        </button>
    );
};

interface StatusComponentProps {
    text?: string;
    sources?: Source[];
    onFilterSources: (sourceIndexes: number[]) => void;
}

const StatusComponent = ({
    text,
    sources,
    onFilterSources
}: StatusComponentProps) => {
    const citationNumbers = (text?.match(/\d+/g) || ["1"])
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isInteger(value) && value > 0);

    const uniqueSourceIndexes = Array.from(
        new Set(citationNumbers.map((value) => value - 1))
    );

    return (
        <CitationIcon
            citations={text || "1"}
            sources={sources}
            onFilterSources={onFilterSources}
            relatedSourceIndexes={uniqueSourceIndexes}
        />
    );
};

const fullContent = css({
    maxHeight: "fit-content",
    paddingBlockEnd: token("space.300")
});

const defaultContent = css({
    maxHeight: "249px",
    overflow: "hidden",
    position: "relative"
});

const blurOverlayStyle = css({
    background: `linear-gradient(180deg, transparent 0%, ${token(
        "elevation.surface.overlay",
        "#FFFFFF"
    )} 50%)`,
    width: "100%",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    insetBlockEnd: 0,
    paddingBlockEnd: "0",
    paddingBlockStart: token("space.800"),
    color: token("color.text", "#172B4D")
});

const showMoreButtonStyle = css({
    "display": "inline-flex",
    "justifyContent": "center",
    "alignItems": "center",
    "height": `${token("space.500")}`,
    "width": "100%",
    "maxWidth": "none", // overrides max-width set on Button component
    "border": `1px solid ${token(
        "color.background.accent.gray.subtle.hovered"
    )}`,
    "borderRadius": `${token("space.500")}`,
    "cursor": "pointer",
    "fontWeight": 400,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": {
        fontWeight: 400
    },

    "&:hover": {
        textDecoration: "none !important"
    }
});

const aiSummaryHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050"),
    marginBlockEnd: token("space.150")
});

const aiSummaryIconWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: token("space.300"),
    height: token("space.300"),
    borderRadius: token("space.050"),
    backgroundColor: token("color.background.accent.blue.subtlest")
});

const markdownContainerStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& div": {
        fontSize: `${token("font.body")} !important`
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a": {
        marginInlineStart: "0 !important"
    }
});

interface AISummaryAnswerProps {
    isExpanded: boolean;
    isTruncated: boolean;
    onToggleExpanded: (isExpanded: boolean) => void;
    onToggleTruncated: (isTruncated: boolean) => void;
    onFilterSources: (sourceIndexes: number[]) => void;
    isSourceFiltered?: boolean;
}

export const AISummaryAnswer = ({
    isExpanded,
    isTruncated,
    onToggleExpanded,
    onToggleTruncated,
    onFilterSources,
    isSourceFiltered
}: AISummaryAnswerProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { summary, sources, query } = useAISummaryContext();

    const statusComponent = useCallback(
        (props: { text?: string; sources?: Source[] }) => (
            <StatusComponent
                {...props}
                sources={sources}
                onFilterSources={onFilterSources}
            />
        ),
        [onFilterSources, sources]
    );

    const renderMarkdownContent = (text: string) => {
        // Use the search-ai approach: encodeAdf + Atlaskit renderer
        // This handles both markdown formatting and citations properly
        return (
            <div css={markdownContainerStyle}>
                <MarkdownRenderer
                    answerString={text}
                    statusComponent={statusComponent}
                    query={query}
                    isSourceFiltered={isSourceFiltered}
                />
            </div>
        );
    };

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            if (contentRef.current) {
                const wrapperHeight = contentRef.current.offsetHeight;
                const contentHeight = contentRef.current.scrollHeight;
                onToggleTruncated(contentHeight > wrapperHeight);
            }
        });

        resizeObserver.observe(contentRef.current);
        return () => resizeObserver.disconnect();
    }, [summary]);

    const handleToggleExpansion = () => {
        onToggleExpanded(!isExpanded);

        createAnalyticsEvent({
            eventName: isExpanded
                ? "searchAiSummaryReadLessClicked"
                : "searchAiSummaryReadMoreClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: isExpanded
                ? "searchAiSummaryReadLess"
                : "searchAiSummaryReadMore",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                labelEnglish: isExpanded ? "Read less" : "Read more"
            }
        }).fire();
    };

    const showMoreText = intl.formatMessage({
        id: "ai.summary.answer.showMoreText",
        defaultMessage: "Show more"
    });

    const aiOverviewText = intl.formatMessage({
        id: "ai.summary.answer.overview",
        defaultMessage: "AI overview"
    });

    return (
        <>
            <div css={aiSummaryHeaderStyle}>
                <div css={aiSummaryIconWrapperStyle}>
                    <AiGenerativeTextSummaryIcon
                        size="medium"
                        label={aiOverviewText}
                        color={token("color.icon.brand")}
                    />
                </div>
                {aiOverviewText}
            </div>
            <div
                ref={contentRef}
                css={[defaultContent, isExpanded && fullContent]}
            >
                {renderMarkdownContent(summary)}
                {isTruncated && !isExpanded && (
                    <div css={blurOverlayStyle}>
                        <Button
                            variant="secondary"
                            css={showMoreButtonStyle}
                            onClick={handleToggleExpansion}
                            aria-label={showMoreText}
                        >
                            {showMoreText}
                        </Button>
                    </div>
                )}
            </div>
            {isExpanded && <AISummaryFooter />}
        </>
    );
};
